"use client";

import { useState } from "react";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { LandingThemePreview } from "@/components/admin/landings/landing-theme-preview";
import {
  BRANDBOOK_LANDING_THEME,
  DEFAULT_LANDING_ROOT_PROPS,
  getAccentPreviewColor,
  LANDING_ACCENT_PALETTES,
  LANDING_COLOR_SCHEMES,
  LANDING_SURFACE_PALETTES,
  surfacePreviewForMode,
  type LandingAccentPalette,
  type LandingColorScheme,
  type LandingPageTheme,
  type LandingSurfacePalette,
} from "@/lib/landing/puck/themes";
import { LandingThemeSummaryChip } from "@/components/admin/landings/landing-theme-summary-chip";
import { Button } from "@astryxdesign/core/Button";

type ThemeValue = Pick<
  LandingPageTheme,
  "accentPalette" | "surfaceLight" | "surfaceDark" | "colorScheme"
>;

type LandingThemePickerProps = {
  value: ThemeValue;
  onChange: (value: ThemeValue) => void;
  mode?: "full" | "sidebar";
};

const PRESET_CARD =
  "rounded-lg border-2 p-1 transition-colors hover:border-primary/50";

function SurfaceOptions({
  label,
  selected,
  dark,
  value,
  onSelect,
  sidebar,
}: {
  label: string;
  selected: LandingSurfacePalette;
  dark: boolean;
  value: ThemeValue;
  onSelect: (value: LandingSurfacePalette) => void;
  sidebar: boolean;
}) {
  return (
    <div className="w-full">
      <Text size="sm" color="secondary" className="mb-2">
        {label}
      </Text>
      {sidebar ? (
        <div className="flex flex-col gap-1.5">
          {LANDING_SURFACE_PALETTES.map((p) => {
            const active = selected === p.value;
            const preview = surfacePreviewForMode(
              p.value as LandingSurfacePalette,
              dark,
            );
            return (
              <button
                key={p.value}
                type="button"
                className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs ${
                  active ? "border-primary bg-muted" : "border-border"
                }`}
                onClick={() => onSelect(p.value as LandingSurfacePalette)}
              >
                <span
                  className="h-6 w-10 shrink-0 rounded border border-black/10"
                  style={{
                    background: preview.background,
                    color: preview.foreground,
                  }}
                />
                <span>{p.label}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {LANDING_SURFACE_PALETTES.map((p) => {
            const active = selected === p.value;
            const previewTheme: LandingPageTheme = {
              title: "",
              ...value,
              ...(dark
                ? { surfaceDark: p.value as LandingSurfacePalette }
                : { surfaceLight: p.value as LandingSurfacePalette }),
            };
            return (
              <button
                key={p.value}
                type="button"
                className={`${PRESET_CARD} text-left ${
                  active ? "border-primary" : "border-border"
                }`}
                onClick={() => onSelect(p.value as LandingSurfacePalette)}
              >
                <LandingThemePreview
                  theme={previewTheme}
                  previewMode={dark ? "dark" : "light"}
                  compact
                />
                <Text size="sm" className="mt-1 px-0.5">
                  {p.label}
                </Text>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function LandingThemePicker({
  value,
  onChange,
  mode = "full",
}: LandingThemePickerProps) {
  const sidebar = mode === "sidebar";
  const [previewTab, setPreviewTab] = useState<"light" | "dark">("light");

  const summaryTheme: LandingPageTheme = {
    title: "",
    ...value,
  };

  return (
    <VStack gap={4} align="start" width="100%">
      <LandingThemeSummaryChip theme={summaryTheme} variant="panel" />

      <div className="w-full rounded-lg border border-border bg-muted/40 p-3">
        <VStack gap={2} align="start" width="100%">
          <Text size="sm" weight="semibold">
            Брендбук Idealitika
          </Text>
          <Text size="sm" color="secondary">
            Красный #D6002E / #E11D48, фоны #FFFFFF · #F5F5F7 · #06080B, текст
            #1D1D1F / #7C7C80, Arial, меню по центру, лого 190/300px, подменю
            20px.
          </Text>
          <Button
            label="Применить брендбук"
            variant="secondary"
            size="sm"
            onClick={() => onChange({ ...BRANDBOOK_LANDING_THEME })}
          />
        </VStack>
      </div>

      <div className="w-full">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Text size="sm" weight="semibold">
            Превью
          </Text>
          <div className="flex gap-1">
            {(["light", "dark"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                className={`rounded-md border px-2 py-0.5 text-xs ${
                  previewTab === tab
                    ? "border-primary bg-muted text-primary"
                    : "border-border text-secondary"
                }`}
                onClick={() => setPreviewTab(tab)}
              >
                {tab === "light" ? "Светлая" : "Тёмная"}
              </button>
            ))}
          </div>
        </div>
        <LandingThemePreview
          theme={summaryTheme}
          previewMode={previewTab}
          compact={sidebar}
        />
      </div>

      <div className="w-full">
        <Text size="sm" color="secondary" className="mb-2">
          Акцент (кнопки, ссылки, бейджи)
        </Text>
        <div className="flex flex-wrap gap-1.5">
          {LANDING_ACCENT_PALETTES.map((p) => {
            const active = value.accentPalette === p.value;
            const color = getAccentPreviewColor(p.value as LandingAccentPalette);
            return (
              <button
                key={p.value}
                type="button"
                title={p.label}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${
                  active ? "border-primary bg-muted" : "border-border"
                }`}
                onClick={() =>
                  onChange({
                    ...value,
                    accentPalette: p.value as LandingAccentPalette,
                  })
                }
              >
                <span
                  className="h-4 w-4 shrink-0 rounded-full border border-black/10"
                  style={{ background: color }}
                />
                <span className="truncate">{p.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <SurfaceOptions
        label="Светлая тема — фон, текст, карточки"
        selected={value.surfaceLight}
        dark={false}
        value={value}
        sidebar={sidebar}
        onSelect={(surfaceLight) => onChange({ ...value, surfaceLight })}
      />

      <SurfaceOptions
        label="Тёмная тема — фон, текст, карточки"
        selected={value.surfaceDark}
        dark
        value={value}
        sidebar={sidebar}
        onSelect={(surfaceDark) => onChange({ ...value, surfaceDark })}
      />

      <div className="w-full">
        <Text size="sm" color="secondary" className="mb-2">
          Какую версию показывать посетителю
        </Text>
        <div className="flex flex-col gap-1.5">
          {LANDING_COLOR_SCHEMES.map((p) => {
            const active = value.colorScheme === p.value;
            return (
              <button
                key={p.value}
                type="button"
                className={`rounded-md border px-3 py-2 text-left text-sm ${
                  active
                    ? "border-primary bg-muted text-primary"
                    : "border-border text-secondary hover:bg-muted"
                }`}
                onClick={() =>
                  onChange({
                    ...value,
                    colorScheme: p.value as LandingColorScheme,
                  })
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
    </VStack>
  );
}

export function defaultThemePickerValue(): ThemeValue {
  return {
    accentPalette: DEFAULT_LANDING_ROOT_PROPS.accentPalette,
    surfaceLight: DEFAULT_LANDING_ROOT_PROPS.surfaceLight,
    surfaceDark: DEFAULT_LANDING_ROOT_PROPS.surfaceDark,
    colorScheme: DEFAULT_LANDING_ROOT_PROPS.colorScheme,
  };
}
